<template>
  <div class="p-4" v-if="widgetsEnabled">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-white">Widgets Admin</h1>
      <div class="flex items-center gap-3">
        <div v-if="licenseBanner" class="text-yellow-200">License limit reached (maxWidgets: {{ license.maxWidgets }})</div>
        <button class="cl-btn-primary" :disabled="!canCreate" @click="openEditor()">Create Widget</button>
      </div>
    </div>
    <div v-if="saveError" class="text-red-400 mb-4">{{ saveError }}</div>

    <!-- Search and View Controls -->
    <div class="flex items-center justify-between mb-4 gap-4">
      <div class="flex items-center gap-3">
        <input 
          v-model="searchQuery" 
          @input="onSearchInput"
          placeholder="Search widgets by name or description..." 
          class="cl-input flex-1 min-w-64"
        />
        <div class="text-blue-100 text-sm">{{ filteredItems.length }} widget(s)</div>
      </div>
      
      <div class="flex items-center gap-2">
        <span class="text-blue-100 text-sm">View:</span>
        <button 
          @click="setViewMode('cards')" 
          class="view-toggle-btn"
          :class="{ 'active': viewMode === 'cards' }"
          title="Card view"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </button>
        <button 
          @click="setViewMode('list')" 
          class="view-toggle-btn"
          :class="{ 'active': viewMode === 'list' }"
          title="List view"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Card View -->
    <div v-if="viewMode === 'cards'" class="grid">
      <div v-for="w in filteredItems" :key="w.id" class="admin-card">
        <div class="flex justify-between mb-2">
          <h3 class="text-white m-0">{{ w.name }}</h3>
          <div class="flex gap-2">
            <button class="cl-btn" @click="openEditor(w)">Edit</button>
            <button class="cl-btn-danger" @click="remove(w.id)">Delete</button>
          </div>
        </div>
        <div class="text-blue-100 mb-3">{{ w.description }}</div>
        <div class="widget-meta">
          <div class="meta-item">
            <span class="meta-label">Label:</span>
            <span class="meta-value">{{ w.labelName || '-' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Printer:</span>
            <span class="meta-value">{{ w.printerName || '-' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Width:</span>
            <span class="meta-value">{{ w.width || 2 }} columns</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Article Mode:</span>
            <span class="meta-value">{{ w.articleMode || 'groupOnly' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="list-view">
      <div class="list-header">
        <div class="list-cell">Name</div>
        <div class="list-cell">Description</div>
        <div class="list-cell">Label</div>
        <div class="list-cell">Printer</div>
        <div class="list-cell">Width</div>
        <div class="list-cell">Actions</div>
      </div>
      <div v-for="w in filteredItems" :key="w.id" class="list-row">
        <div class="list-cell font-medium" data-label="Name">{{ w.name }}</div>
        <div class="list-cell" data-label="Description">{{ w.description || '-' }}</div>
        <div class="list-cell" data-label="Label">{{ w.labelName || '-' }}</div>
        <div class="list-cell" data-label="Printer">{{ w.printerName || '-' }}</div>
        <div class="list-cell" data-label="Width">{{ w.width || 2 }} cols</div>
        <div class="list-cell" data-label="Actions">
          <div class="flex gap-2">
            <button class="cl-btn-small" @click="openEditor(w)">Edit</button>
            <button class="cl-btn-danger-small" @click="remove(w.id)">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <WidgetEditor v-if="showEditor" :value="editing" @close="closeEditor" @save="save"/>
  </div>
</template>

<script>
import WidgetEditor from "@/views/WidgetEditor.vue";
import { getPreference, setPreference } from "@/utils/userPreferences";

export default {
  name: "WidgetsAdmin",
  components: { WidgetEditor },
  data: () => ({
    showEditor: false,
    editing: null,
    searchQuery: '',
    viewMode: 'cards',
    saveError: ''
  }),
  computed: {
    items() { return this.$store.getters["WIDGETS/items"]; },
    license() { return this.$store.getters["WIDGETS/license"]; },
    labels() { return this.$store.getters["LABELS/LABELS"]; },
    printers() { return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"]; },
    canCreate() {
      const max = this.license.maxWidgets;
      return !max || max <= 0 || this.items.length < max;
    },
    licenseBanner() { return !this.canCreate; },
    filteredItems() {
      if (!this.searchQuery.trim()) {
        return this.items;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.items.filter(widget => 
        (widget.name && widget.name.toLowerCase().includes(query)) ||
        (widget.description && widget.description.toLowerCase().includes(query))
      );
    },
    widgetsEnabled() {
      const subscriptionData = this.$store.getters["SUBSCRIPTIONS/SUBSCRIPTION_DATA"];
      return subscriptionData && subscriptionData.widgetsEnabled === true;
    }
  },
  methods: {
    openEditor(item) { this.editing = item ? { ...item } : null; this.showEditor = true; },
    closeEditor() { this.showEditor = false; this.editing = null; },
    async save(widget) {
      const label = this.labels.find(l => l.id === widget.labelId);
      const printer = (this.printers || []).find(p => p.id === widget.printerId || p.printerId === widget.printerId);
      const enriched = {
        favorite: widget.favorite === true ? true : false,
        favoriteFor: Array.isArray(widget.favoriteFor) ? widget.favoriteFor : [],
        labelName: label ? label.name : widget.labelName,
        printerName: printer ? (printer.name || printer.displayName || printer.display) : widget.printerName,
        ...widget
      };
      this.saveError = "";
      try {
        if (widget.id) await this.$store.dispatch("WIDGETS/updateWidget", enriched);
        else await this.$store.dispatch("WIDGETS/createWidget", { ...enriched, id: `widget-${Date.now()}` });
        this.closeEditor();
      } catch (e) {
        const data = e.response && e.response.data;
        this.saveError = (data && (data.Message || data.message)) || "Could not save widget. The widget limit may have been reached.";
      }
    },
    remove(id) { this.$store.dispatch("WIDGETS/deleteWidget", id); },
    
    // View mode handling
    setViewMode(mode) {
      this.viewMode = mode;
      setPreference('widgetsAdmin', 'viewMode', mode);
    },
    
    // Search handling
    onSearchInput() {
      setPreference('widgetsAdmin', 'searchQuery', this.searchQuery);
    },
    
    // Load user preferences
    loadPreferences() {
      this.viewMode = getPreference('widgetsAdmin', 'viewMode', 'cards');
      this.searchQuery = getPreference('widgetsAdmin', 'searchQuery', '');
    }
  },
  async mounted() {
    // Load user preferences first
    this.loadPreferences();
    
    // Load data
    this.$store.dispatch("WIDGETS/loadWidgets");
    this.$store.dispatch("LABELS/GET_LABELS");
    this.$store.dispatch("PRINTERS/GET_INSTALLED_PRINTERS");
    await this.$store.dispatch("WIDGETS/loadLicense");
  }
};
</script>

<style scoped>
/* Grid Layout */
.grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); 
  gap: 20px; 
}

@media (max-width: 1200px) { 
  .grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); } 
}

@media (max-width: 700px) { 
  .grid { grid-template-columns: 1fr; } 
}

/* Card Styling */
.admin-card { 
  border: 1px solid rgba(255,255,255,0.15); 
  border-radius: 12px; 
  padding: 20px; 
  background: linear-gradient(135deg, #0b2748 0%, #0e315a 100%);
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.admin-card:hover {
  border-color: rgba(255,255,255,0.3);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* Widget Meta Information */
.widget-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.meta-value {
  font-size: 0.875rem;
  color: #e2e8f0;
  font-weight: 400;
}

/* Search Input */
.cl-input { 
  background: #0a213f; 
  color: #fff; 
  border: 1px solid #28518b; 
  border-radius: 8px; 
  padding: 10px 12px; 
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.cl-input:focus {
  outline: none;
  border-color: #2d7df4;
  box-shadow: 0 0 0 3px rgba(45, 125, 244, 0.1);
}

.cl-input::placeholder {
  color: #64748b;
}

/* View Toggle Buttons */
.view-toggle-btn {
  background: #0a213f;
  color: #94a3b8;
  border: 1px solid #28518b;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-toggle-btn:hover {
  background: #0e315a;
  color: #e2e8f0;
  border-color: #2d7df4;
}

.view-toggle-btn.active {
  background: #2d7df4;
  color: #fff;
  border-color: #2d7df4;
}

/* Buttons */
.cl-btn-primary { 
  background: #2d7df4; 
  color: #fff; 
  border: none; 
  padding: 10px 16px; 
  border-radius: 8px; 
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.cl-btn-primary:hover {
  background: #1d4ed8;
}

.cl-btn-primary:disabled {
  background: #64748b;
  cursor: not-allowed;
}

.cl-btn { 
  background: #0a213f; 
  color: #fff; 
  border: 1px solid #28518b; 
  padding: 8px 12px; 
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cl-btn:hover {
  background: #0e315a;
  border-color: #2d7df4;
}

.cl-btn-danger { 
  background: #dc2626; 
  color: #fff; 
  border: none; 
  padding: 8px 12px; 
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.cl-btn-danger:hover {
  background: #b91c1c;
}

/* List View */
.list-view {
  background: linear-gradient(135deg, #0b2748 0%, #0e315a 100%);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.15);
  overflow: hidden;
}

.list-header {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 0.8fr 1.2fr;
  gap: 16px;
  padding: 16px 20px;
  background: rgba(255,255,255,0.05);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  font-weight: 600;
  color: #e2e8f0;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.list-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 0.8fr 1.2fr;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  transition: background-color 0.2s ease;
}

.list-row:hover {
  background: rgba(255,255,255,0.05);
}

.list-row:last-child {
  border-bottom: none;
}

.list-cell {
  color: #e2e8f0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.cl-btn-small {
  background: #0a213f;
  color: #fff;
  border: 1px solid #28518b;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cl-btn-small:hover {
  background: #0e315a;
  border-color: #2d7df4;
}

.cl-btn-danger-small {
  background: #dc2626;
  color: #fff;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.cl-btn-danger-small:hover {
  background: #b91c1c;
}

/* Responsive adjustments for list view */
@media (max-width: 1200px) {
  .list-header,
  .list-row {
    grid-template-columns: 2fr 1.5fr 1fr 1fr 0.8fr 1.2fr;
  }
}

@media (max-width: 900px) {
  .list-header,
  .list-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .list-cell {
    justify-content: space-between;
    padding: 4px 0;
  }
  
  .list-cell::before {
    content: attr(data-label);
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }
}
</style>


